import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onFinish", "children", "videoType", "videoId", "autoPlay", "playSpeed", "language", "onProgress", "onPlay", "onTimeUpdate", "videoDuration", "currentTime", "completedVideoView"],
  _excluded2 = ["contentId", "lessonId", "trackId", "language"],
  _excluded3 = ["shortId", "shortName", "playlistId"],
  _excluded4 = ["shortId"];
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import omit from 'hs-lodash/omit';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import Raven from 'raven-js';
import getLang from 'LearningCenterUI/utils/getLang';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import { TrackingContentTypes } from '../../../constants/Tracking';
import ContentViewport from '../../../components/ContentViewport';
import { useVideo } from '../../../features/video/videoQueries';
import { Metrics } from '../../../Metrics';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import { useShort, useStartShort } from '../../shortContent/shortContentQueries';
import { useShortVideo } from '../../shortContent/shortContentHooks';
import { EmbeddedVideo } from './EmbeddedVideo';
import { useAutoClearVideoVTS, useVideoParams, useVideoPlayerConfig } from '../videoHooks';
import { convertToMinutes } from '../../../utils/utils';
import UsageTracker from '../../../utils/UsageTracker';
import { usePostMlRecommendationsContentEngagedFeedback } from '../../recommendations/recommendationsQueries';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks';
import { usePlaylist } from '../../savedItems/savedItemsQueries';
import { useIsHubspotPlaylist } from '../../savedItems/savedItemsHooks';
import { updateContentProgress } from '../../userProgress/userProgressQueries';
import { useLearnByDoing } from '../../toolRecommendations';
import { LearnByDoingCTAScreen } from '../../toolRecommendations';
import { useSearchState } from '../../search/searchStore';
import { useRequestLearnWhileDoing } from '../../toolRecommendations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CompletedVideoView = styled(UIFlex).withConfig({
  displayName: "VideoPlayer__CompletedVideoView",
  componentId: "sc-1qr5wb8-0"
})(["position:absolute;background-color:", ";top:0;width:100%;height:100%;color:", ";"], theme.colors.MONOLITH, theme.colors.OLAF);
const ReplayIcon = styled(UIIcon).attrs({
  name: 'rotate',
  className: 'video-replay-button'
}).withConfig({
  displayName: "VideoPlayer__ReplayIcon",
  componentId: "sc-1qr5wb8-1"
})(["color:", ";position:absolute;cursor:pointer;bottom:12px;left:12px;font-size:22px;transform:scaleX(-1);"], theme.colors.OLAF);
const VideoPlayerContext = /*#__PURE__*/createContext({
  isShowingCompletedScreen: false,
  showCompletedScreen: () => {},
  hideCompletedScreen: () => {}
});
export const useVideoPlayerContext = () => useContext(VideoPlayerContext);
export const VideoPlayerProvider = ({
  children
}) => {
  const [isShowingCompletedScreen, setShowCompletedVideoScreen] = useState(false);
  const showCompletedScreen = () => setShowCompletedVideoScreen(true);
  const hideCompletedScreen = () => setShowCompletedVideoScreen(false);
  const contextState = {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  };
  return /*#__PURE__*/_jsx(VideoPlayerContext.Provider, {
    value: contextState,
    children: children
  });
};
const getStartTime = ({
  startTimeFromUrl,
  startTimeFromProps,
  videoDuration
}) => {
  const desiredTime = startTimeFromProps || startTimeFromUrl;
  if (desiredTime && videoDuration && desiredTime < videoDuration) return desiredTime;
  return undefined;
};
const VideoPlayerBase = _ref => {
  let {
      onFinish,
      children,
      videoType,
      videoId,
      autoPlay,
      playSpeed,
      language,
      onProgress,
      onPlay,
      onTimeUpdate,
      videoDuration,
      currentTime: startTimeFromProps,
      completedVideoView
    } = _ref,
    itemProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    clearLastClickedSearchResultItem,
    lastClickedSearchResultItem
  } = useSearchState();
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist);
  useAutoClearVideoVTS();
  const {
    subtitleLanguage
  } = useVideoPlayerConfig();
  const secondsElapsed = useRef(0);
  const [hasClickedPlayForVideo, setHasClickedPlayForVideo] = useState(false);
  const hasMarkedProgress = useRef(false);
  const {
    startTime: startTimeFromUrl
  } = useVideoParams();
  const {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  } = useVideoPlayerContext();
  const {
    isVideoFinished,
    handleEnableLearnWhileDoingScreen,
    handleVideoPlayed,
    handleVideoFinished
  } = useRequestLearnWhileDoing();
  const startTime = getStartTime({
    startTimeFromUrl,
    startTimeFromProps,
    videoDuration: videoDuration
  });
  const buildWhatExtraJson = useCallback((action, props) => {
    return Object.assign({
      action,
      contentLanguage: language,
      contentType: TrackingContentTypes.VIDEO,
      playerType: videoType
    }, playlistId && {
      playlistId
    }, playlist && {
      isHubspotPlaylist
    }, playlist && {
      playlistName: playlist.name
    }, props, omit(itemProps, 'PlayerComponent', 'onPlay'));
  }, [language, videoType, itemProps, playlist, playlistId, isHubspotPlaylist]);
  const handleVideoPlay = useCallback(() => {
    if (!hasClickedPlayForVideo) {
      let extraTrackingInfo;
      // extra tracking for search results, to be removed once searchv1 is gone
      if (lastClickedSearchResultItem) {
        const id = Number(itemProps.trackId || itemProps.lessonId || itemProps.shortId);
        const type = itemProps.trackId ? 'TRACK' : itemProps.lessonId ? 'LESSON' : 'SHORT_FORM_CONTENT';
        if (id === lastClickedSearchResultItem.item.id && type === lastClickedSearchResultItem.item.type) {
          extraTrackingInfo = {
            position: lastClickedSearchResultItem.position
          };
          clearLastClickedSearchResultItem();
        }
      }
      setHasClickedPlayForVideo(true);
      if (onPlay) {
        onPlay();
      }
      UsageTracker.track('contentInteraction', Object.assign({}, buildWhatExtraJson('video-play'), extraTrackingInfo));
    }
    Metrics.counter('hs-video-played').increment();
  }, [buildWhatExtraJson, clearLastClickedSearchResultItem, hasClickedPlayForVideo, itemProps.lessonId, itemProps.shortId, itemProps.trackId, lastClickedSearchResultItem, onPlay]);
  const handleUpdateProgress = useCallback(props => {
    UsageTracker.track('contentProgress', buildWhatExtraJson('video-progression', props));
    if (onProgress) onProgress();
  }, [buildWhatExtraJson, onProgress]);
  const handleVideoFinish = useCallback(() => {
    UsageTracker.track('contentInteraction', buildWhatExtraJson('video-finished'));
    Metrics.counter('hs-video-finished').increment();
    const viewDuration = videoDuration && convertToMinutes(videoDuration - secondsElapsed.current);
    if (onFinish) {
      onFinish();
      if (!hasMarkedProgress && viewDuration) handleUpdateProgress({
        viewDuration
      });
    }
    showCompletedScreen();
  }, [buildWhatExtraJson, handleUpdateProgress, hasMarkedProgress, onFinish, showCompletedScreen, videoDuration]);
  useEffect(() => {
    if (videoId) hasMarkedProgress.current = false;
    setHasClickedPlayForVideo(false);
  }, [videoId]);
  const handleReplayClick = () => {
    hideCompletedScreen();
    if (itemProps.shortId) {
      handleEnableLearnWhileDoingScreen();
      handleVideoPlayed(false);
      handleVideoFinished(false);
    }
  };
  const shouldRenderCompletedVideoView = completedVideoView && isShowingCompletedScreen || isVideoFinished;
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "embedded-video",
    children: [/*#__PURE__*/_jsxs(ContentViewport, {
      wrapperStyle: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(EmbeddedVideo, Object.assign({
        autoPlay: autoPlay,
        language: language,
        onActivation: handleUpdateProgress,
        onFinish: handleVideoFinish,
        onPlay: handleVideoPlay,
        onTimeUpdate: onTimeUpdate,
        playSpeed: playSpeed,
        renderContext: "academy",
        startTime: startTime,
        subtitleLanguage: subtitleLanguage
      }, itemProps)), shouldRenderCompletedVideoView && /*#__PURE__*/_jsxs(CompletedVideoView, {
        children: [/*#__PURE__*/_jsx(ReplayIcon, {
          onClick: handleReplayClick
        }), completedVideoView]
      })]
    }), children]
  });
};
const VideoPlayerTrackLesson = _ref2 => {
  let {
      contentId,
      lessonId,
      trackId,
      language: languageProp
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    postMlRecommendationsContentEngagedFeedback
  } = usePostMlRecommendationsContentEngagedFeedback();
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const language = languageProp || (searchParamLanguage || getLang()).toLowerCase();
  const handleOnProgress = useCallback(() => {
    if (language) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateContentProgress({
        contentId: Number(contentId),
        lessonId: Number(lessonId),
        trackId: Number(trackId),
        language
      });
    }
  }, [contentId, language, lessonId, trackId]);
  const handleOnPlay = useCallback(() => {
    var _postMlRecommendation;
    if ((trackId || lessonId) && contentId) (_postMlRecommendation = postMlRecommendationsContentEngagedFeedback({
      item: {
        id: Number(trackId || lessonId),
        type: trackId ? 'TRACK' : 'LESSON'
      },
      contentId: Number(contentId)
    })) === null || _postMlRecommendation === void 0 || _postMlRecommendation.catch(Raven.captureException);
  }, [contentId, lessonId, postMlRecommendationsContentEngagedFeedback, trackId]);
  const videoProps = useVideo({
    contentId,
    lessonId,
    trackId,
    language
  });
  return /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
    contentId: contentId,
    language: language,
    lessonId: lessonId,
    onPlay: handleOnPlay,
    onProgress: handleOnProgress,
    trackId: trackId
  }, props, videoProps));
};
const VideoPlayerShort = _ref3 => {
  let {
      shortId,
      shortName,
      playlistId
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    isVideoFinished
  } = useRequestLearnWhileDoing();
  const videoProps = useShortVideo({
    shortId: shortId,
    skip: !shortId
  });
  const {
    shouldRenderLearnByDoingScreen,
    updatedVideoProps
  } = useLearnByDoing();
  const {
    short,
    shortLoading
  } = useShort(shortId, !shortId);
  const language = short === null || short === void 0 ? void 0 : short.language;
  const [startShort] = useStartShort(shortId, language, playlistId);
  const {
    postMlRecommendationsContentEngagedFeedback
  } = usePostMlRecommendationsContentEngagedFeedback();
  const handleOnPlay = useCallback(() => {
    var _postMlRecommendation2;
    startShort().catch(Raven.captureException);
    if (shortId) (_postMlRecommendation2 = postMlRecommendationsContentEngagedFeedback({
      item: {
        id: shortId,
        type: 'SHORT_FORM_CONTENT'
      },
      contentId: shortId
    })) === null || _postMlRecommendation2 === void 0 || _postMlRecommendation2.catch(Raven.captureException);
  }, [postMlRecommendationsContentEngagedFeedback, shortId, startShort]);
  if (!videoProps || !shortId || !language) return null;
  if (shortLoading) return /*#__PURE__*/_jsx(UILoadingOverlay, {
    contextual: true,
    layout: "centered",
    use: "spinner"
  });
  const {
    previewThumbnailRectangleUrl
  } = short;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: shouldRenderLearnByDoingScreen && previewThumbnailRectangleUrl && !isVideoFinished ? /*#__PURE__*/_jsx(LearnByDoingCTAScreen, {
      backgroundImage: previewThumbnailRectangleUrl,
      otgId: shortId
    }) : /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
      language: language,
      onPlay: handleOnPlay,
      shortId: shortId,
      shortName: shortName
    }, props, videoProps, updatedVideoProps))
  });
};
export const VideoPlayer = _ref4 => {
  let {
      shortId
    } = _ref4,
    props = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  if (shortId) return /*#__PURE__*/_jsx(VideoPlayerShort, Object.assign({
    shortId: shortId
  }, props));else return /*#__PURE__*/_jsx(VideoPlayerTrackLesson, Object.assign({}, props));
};
export default VideoPlayer;